import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useAuth } from '../../providers/AuthProvider';
import { FederatedButtons } from './FederatedButtons';
import { OrSeparator } from './OrSeparator';
import { AppLogo } from '../AppLogo';
import { getPrivacyUrl } from '../../services/runtime-config';


interface SignInFormProps {
  /** Switch to the sign-up view. */
  onSwitchToSignUp: () => void;
  /** Optional success indication carried over from a completed verification. */
  successMessage?: string | null;
}

/**
 * Email + password sign-in form, plus the three federated provider controls.
 * Preserves the existing sign-in behaviour (calls `login`); the redirect to `/`
 * is handled by the parent `Login` via the `isAuthenticated` guard (Req 4.1,
 * 4.5).
 *
 * Validates: Requirements 4.1, 4.5, 5.1, 5.2, 10.5, 10.6.
 */
export function SignInForm({
  onSwitchToSignUp,
  successMessage,
}: SignInFormProps) {
  // Privacy information URL, resolved from runtime config (per-env
  // marketing/privacy URL) with a built-in fallback (Path 2 domain migration).
  const PRIVACY_URL = getPrivacyUrl();
  const { login, isLoading, error } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const headingRef = useRef<HTMLHeadingElement>(null);

  // Place initial focus on the view heading when this view mounts (Req 10.6).
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    await login(email, password);
  };

  return (
    <form
      onSubmit={handleSubmit}
      style={{ width: '100%', maxWidth: '400px' }}
      aria-label="Login form"
    >
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
        <AppLogo size={56} />
      </div>
      <h1 ref={headingRef} className="focus-target" tabIndex={-1} style={{ marginBottom: '1.5rem' }}>
        Sign In
      </h1>

      {successMessage && (
        <div
          role="status"
          style={{ color: 'var(--color-success)', marginBottom: '1rem' }}
        >
          {successMessage}
        </div>
      )}

      {error && (
        <div
          role="alert"
          style={{ color: 'var(--color-danger)', marginBottom: '1rem' }}
        >
          {error}
        </div>
      )}

      <div style={{ marginBottom: '1rem' }}>
        <label
          htmlFor="email"
          style={{ display: 'block', marginBottom: '0.25rem' }}
        >
          Email
        </label>
        <input
          id="email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoComplete="email"
          style={{ width: '100%' }}
        />
      </div>

      <div style={{ marginBottom: '1.5rem' }}>
        <label
          htmlFor="password"
          style={{ display: 'block', marginBottom: '0.25rem' }}
        >
          Password
        </label>
        <input
          id="password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          autoComplete="current-password"
          style={{ width: '100%' }}
        />
      </div>

      <button
        type="submit"
        disabled={isLoading}
        style={{
          width: '100%',
          padding: '0.75rem',
          backgroundColor: 'var(--color-brand)',
          color: 'var(--color-on-brand)',
          border: 'none',
          borderRadius: '4px',
          cursor: isLoading ? 'not-allowed' : 'pointer',
          fontSize: '1rem',
          opacity: isLoading ? 0.7 : 1,
        }}
      >
        {isLoading ? 'Signing in...' : 'Sign In'}
      </button>

      <OrSeparator />

      <FederatedButtons />

      <p style={{ marginTop: '1.5rem', textAlign: 'center' }}>
        Don&apos;t have an account?{' '}
        <button
          type="button"
          onClick={onSwitchToSignUp}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            color: 'var(--color-link)',
            cursor: 'pointer',
            textDecoration: 'underline',
            fontSize: 'inherit',
          }}
        >
          Create an account
        </button>
      </p>

      <p style={{ marginTop: '0.75rem', textAlign: 'center' }}>
        <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">
          Privacy information
        </a>
      </p>
    </form>
  );
}
